// Darstellung: Design (modern | classic) und Farbschema (system | light | dark).
// Wird lokal zwischengespeichert (sofort beim Start, ohne Aufblitzen) und für angemeldete
// Nutzer zusätzlich auf dem Server (gilt dann auf allen Geräten).
import { useEffect, useState } from 'react';
import apiService from '../services/apiService';

const DESIGN_KEY = 'btmx.design';
const THEME_KEY = 'btmx.theme';

export const DESIGNS = ['modern', 'classic'];
export const THEMES = ['system', 'light', 'dark'];

// Der Dunkelmodus wird erst aktiv, wenn die Seiten auf die Design-Tokens umgestellt sind
// (Etappen 4/5). Bis dahin wird die Auswahl gespeichert, die App bleibt aber hell.
export const DARK_MODE_READY = true;

const media = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null;

function read(key, allowed, fallback) {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try { localStorage.setItem(key, value); } catch { /* Speicher gesperrt */ }
}

export const getDesign = () => read(DESIGN_KEY, DESIGNS, 'modern');
export const getTheme = () => read(THEME_KEY, THEMES, 'system');

/** Tatsächlich angezeigtes Farbschema */
export function resolveTheme(theme = getTheme()) {
  if (!DARK_MODE_READY) return 'light';
  return theme === 'dark' || (theme === 'system' && media?.matches) ? 'dark' : 'light';
}

export function applyAppearance() {
  const root = document.documentElement;
  root.dataset.design = getDesign();
  root.dataset.theme = resolveTheme();
  window.dispatchEvent(new Event('appearance-changed'));
}

function saveToServer(patch) {
  apiService.put('/user/preferences', patch).catch(() => { /* offline: lokal bleibt gespeichert */ });
}

export function setDesign(design) {
  if (!DESIGNS.includes(design)) return;
  write(DESIGN_KEY, design);
  applyAppearance();
  saveToServer({ design });
}

export function setTheme(theme) {
  if (!THEMES.includes(theme)) return;
  write(THEME_KEY, theme);
  applyAppearance();
  saveToServer({ theme });
}

/** Nach dem Anmelden: Einstellungen vom Server übernehmen (Server gewinnt, falls gesetzt) */
export async function loadAppearanceFromServer() {
  try {
    const { preferences } = await apiService.get('/user/preferences');
    if (DESIGNS.includes(preferences?.design)) write(DESIGN_KEY, preferences.design);
    if (THEMES.includes(preferences?.theme)) write(THEME_KEY, preferences.theme);
    applyAppearance();
    return preferences || {};
  } catch {
    return null;
  }
}

// Systemwechsel (z. B. Handy wechselt abends in den Dunkelmodus) live übernehmen
media?.addEventListener?.('change', () => {
  if (getTheme() === 'system') applyAppearance();
});

/** Aktuelles Design als React-State (aktualisiert sich beim Umschalten) */
export function useDesign() {
  const [design, setDesignState] = useState(getDesign);
  useEffect(() => {
    const sync = () => setDesignState(getDesign());
    window.addEventListener('appearance-changed', sync);
    return () => window.removeEventListener('appearance-changed', sync);
  }, []);
  return design;
}
